import { computePosition, autoUpdate, inline, shift, flip, offset, arrow, Placement } from '@floating-ui/dom';
import { generateId } from '../helpers/generateId.js';

export class Tooltip {
	id: string;
	root: HTMLElement;
	tooltip: HTMLElement;
	tooltipContent: HTMLElement;
	arrow: HTMLElement;

	dismountObserver?: MutationObserver;

	container?: HTMLElement;
	placement?: Placement;
	width?: string;
	theme?: string;

	delayTimer?: number;
	autoUpdateCleanup?: () => void;

	constructor(root: HTMLElement) {
		this.id = generateId('tooltip');

		this.root = root;
		this.root.setAttribute('aria-describedby', this.id);
		this.root.classList.add('tooltip-root');

		const tooltip = document.createElement('div');
		tooltip.className = 'tooltip';
		tooltip.id = this.id;
		tooltip.role = 'tooltip';
		this.tooltip = tooltip;

		const tooltipContent = document.createElement('div');
		tooltipContent.className = 'tooltip-content';
		this.tooltipContent = tooltipContent;
		this.tooltip.appendChild(tooltipContent);

		const arrow = document.createElement('span');
		arrow.className = 'tooltip-arrow';
		this.arrow = arrow;
		this.tooltip.appendChild(arrow);

		// parse tooltip options
		this.placement = this.root.getAttribute('data-tooltip-placement') as Placement || undefined;
		this.width = this.root.getAttribute('data-tooltip-width') || undefined;
		if (this.width) this.tooltip.style.maxWidth = this.width;
		this.theme = this.root.getAttribute('data-tooltip-theme') || undefined;
		if (this.theme) this.tooltip.classList.add(`theme-${this.theme}`);
		const containerSelector = this.root.getAttribute('data-tooltip-container');
		const container = containerSelector ? document.querySelector(containerSelector) : null;
		this.container = container as HTMLElement || undefined;

		this.updateContent();

		this.root.addEventListener('mouseover', this.delayedShow);
		this.root.addEventListener('mouseout', this.hide);
		this.root.addEventListener('focus', this.show);
		this.root.addEventListener('blur', this.hide);
		this.root.addEventListener('touchstart', this.onTouchStart);
		this.root.addEventListener('touchend', this.onTouchEnd);
	}

	updateContent = () => {
		let content = this.root.getAttribute('data-tooltip') || '';

		if (this.root.title) {
			content = this.root.title;
			this.root.setAttribute('data-tooltip', content);
			this.root.removeAttribute('title');
		}

		if (this.root.hasAttribute('data-tooltip-html')) this.tooltipContent.innerHTML = content;
		else this.tooltipContent.innerText = content.replaceAll(/\s+/g, ' ');
	};

	update = async () => {
		const { x, y, middlewareData, placement } = await computePosition(this.root, this.tooltip, {
			placement: this.placement || 'top',
			middleware: [
				inline(),
				shift({ padding: 16 }),
				offset(12),
				flip(),
				arrow({ element: this.arrow, padding: 5 })
			]
		});

		this.tooltip.style.left = `${x}px`;
		this.tooltip.style.top = `${y}px`;

		if (middlewareData.arrow) {
			const side = placement.split('-')[0];
			const staticSide = {
				top: 'bottom',
				right: 'left',
				bottom: 'top',
				left: 'right'
			}[side] || 'top';

			const { x, y } = middlewareData.arrow;
			this.arrow.style.left = x != null ? `${x}px` : '';
			this.arrow.style.top = y != null ? `${y}px` : '';
			this.arrow.style.setProperty(staticSide, `${-this.arrow.offsetWidth / 2}px`);
		}
	};

	show = () => {
		this.updateContent();

		// If there is a container provided, render the tooltip there.
		// Otherwise, put it beside the root element.
		if (this.container) this.container.appendChild(this.tooltip);
		else this.root.insertAdjacentElement('afterend', this.tooltip);

		// Start a document-wide mutation observer to check if the root element is dismounted from
		// the document.
		if (this.dismountObserver) this.dismountObserver.disconnect();
		this.dismountObserver = new MutationObserver(() => {
			if (!this.root.isConnected) {
				this.hide();
				this.dismountObserver?.disconnect();
				delete this.dismountObserver;
			}
		});
		this.dismountObserver.observe(document, { childList: true, subtree: true });

		// Render the tooltip & auto-update on position changes.
		this.autoUpdateCleanup = autoUpdate(this.root, this.tooltip, this.update);
	};

	hide = () => {
		if (this.delayTimer) {
			window.clearTimeout(this.delayTimer);
			delete this.delayTimer;
		}

		if (this.dismountObserver) this.dismountObserver.disconnect();
		if (this.tooltip.isConnected) this.tooltip.parentElement?.removeChild(this.tooltip);
		if (this.autoUpdateCleanup) this.autoUpdateCleanup();
	};

	delayedShow = () => {
		this.delayTimer = window.setTimeout(() => {
			this.show();
		}, 250);
	};

	onTouchStart = () => {
		this.root.classList.add('tooltip-touch-active');
		this.delayedShow();
	};

	onTouchEnd = () => {
		this.root.classList.remove('tooltip-touch-active');
		this.hide();
	};
}

export const init = () => {
	const tooltips = Array.from(document.querySelectorAll<HTMLElement>('[data-tooltip]'));
	if (!window.StandardUI.tooltip) window.StandardUI.tooltip = { init, instances: [] };

	for (const root of tooltips) {
		if (!window.StandardUI.tooltip.instances.find(x => x.root === root)) {
			const tooltip = new Tooltip(root);
			window.StandardUI.tooltip.instances.push(tooltip);
		}
	}
};

export default {
	init
};
